import StatCard from "../../../../parts/cards/StatCard";
import { formatNumber } from "../../../../utils/format-sub";
import CsvExportLink from "./CsvExportLink";
import GrowthSection from "./GrowthSection";
import { rowsToCsv } from "./incomeChartData";

/**
 * Requirements §5.8: per-coupon performance. Redemptions, discount given
 * and revenue are period numbers; new subscribers and still-active are
 * all-time for the code — each column header states its basis so the two
 * kinds never blur into one implied "this period" story. Return per $1 is
 * "so far" and structurally understates every code; the footnote says so.
 */
// `context` picks the *_press copy ("members" for "subs") via i18next's
// context lookup, falling back to the base key for PUSH.
const GrowthCoupons = ({
  rows,
  summary,
  currency,
  badges,
  rangeLabel,
  context,
  t,
}) => {
  if (!summary) return null;
  const money = (v) => formatNumber(v || 0, currency);
  const terms = (row) => {
    const off =
      row.percentOff != null ? `${row.percentOff}%` : money(row.amountOff);
    return t(`growth.coupons.termsByDuration.${row.duration || "once"}`, {
      off,
      count: row.durationInMonths || 0,
    });
  };
  const stillActivePct = (row) =>
    row.newSubsAllTime > 0
      ? `${Math.round((row.stillActiveAllTime / row.newSubsAllTime) * 100)}%`
      : null;

  const csv = rowsToCsv(
    [
      t("growth.coupons.code"),
      t("growth.coupons.terms"),
      t("growth.coupons.status"),
      t("growth.coupons.redemptions"),
      t("growth.coupons.discountGiven"),
      t("growth.coupons.revenue"),
      t("growth.coupons.newSubs", { context }),
      t("growth.coupons.stillActive"),
    ],
    rows.map((r) => [
      r.name || r.couponId,
      terms(r),
      t(`growth.coupons.statusLabel.${r.valid ? "active" : "expired"}`),
      r.redemptionsInPeriod,
      -r.discountGivenInPeriod,
      r.revenueFromRedeemersInPeriod,
      r.newSubsAllTime,
      r.stillActiveAllTime,
    ])
  );

  return (
    <GrowthSection
      title={t("growth.coupons.title")}
      subtitle={t("growth.coupons.subtitle")}
      tiles={
        <>
          <StatCard
            title={t("growth.coupons.activeCodes")}
            badge={badges.now}
            current={summary.activeCodes}
            caption={t("growth.coupons.expiredCaption", {
              count: summary.expiredCodes,
            })}
          />
          <StatCard
            title={t("growth.coupons.redemptions")}
            badge={badges.period}
            current={summary.redemptionsInPeriod}
            caption={t("growth.coupons.newSubsCaption", {
              context,
              count: summary.newSubsInPeriod,
            })}
          />
          <StatCard
            title={t("growth.coupons.discountGiven")}
            badge={badges.period}
            current={summary.discountGivenInPeriod}
            formatValue={money}
            caption={
              summary.discountShareOfGross == null
                ? undefined
                : t("growth.coupons.shareOfGross", {
                    pct: summary.discountShareOfGross,
                  })
            }
          />
          <StatCard
            title={t("growth.coupons.revenueFromRedeemers")}
            badge={badges.period}
            current={summary.revenueFromRedeemersInPeriod}
            formatValue={money}
            caption={
              summary.roiSoFar == null
                ? undefined
                : t("growth.coupons.roiCaption", {
                    value: money(summary.roiSoFar),
                  })
            }
          />
        </>
      }
    >
      <section className="incomes-container growth-card mt-6">
        <header className="table-card__header">
          <h3>{t("growth.coupons.tableTitle")}</h3>
          {badges.period}
          <span className="grow" />
          <CsvExportLink
            csv={rows.length ? csv : ""}
            filename="coupons.csv"
            t={t}
          />
        </header>
        {/* biome-ignore lint/a11y/useValidAriaRole: report-table used for structure */}
        <table role="report-table">
          <thead>
            <tr>
              <th>{t("growth.coupons.code")}</th>
              <th>{t("growth.coupons.terms")}</th>
              <th>{t("growth.coupons.status")}</th>
              <th>
                {t("growth.coupons.redemptions")}
                <small className="table-card__subtitle">{rangeLabel}</small>
              </th>
              <th>
                {t("growth.coupons.discountGiven")}
                <small className="table-card__subtitle">{rangeLabel}</small>
              </th>
              <th>
                {t("growth.coupons.revenue")}
                <small className="table-card__subtitle">{rangeLabel}</small>
              </th>
              <th>
                {t("growth.coupons.newSubs", { context })}
                <small className="table-card__subtitle">
                  {t("growth.coupons.allTime")}
                </small>
              </th>
              <th>
                {t("growth.coupons.stillActive")}
                <small className="table-card__subtitle">
                  {t("growth.coupons.allTime")}
                </small>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={8}>
                  <div className="body-table-empty">
                    <p>{t("growth.coupons.empty")}</p>
                  </div>
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr key={r.couponId}>
                <td>{r.name || r.couponId}</td>
                <td>{terms(r)}</td>
                <td>
                  <span
                    className="pill"
                    data-status={r.valid ? "active" : "expired"}
                  >
                    {t(
                      `growth.coupons.statusLabel.${r.valid ? "active" : "expired"}`
                    )}
                  </span>
                </td>
                <td>{r.redemptionsInPeriod}</td>
                <td className="text-(--danger)">
                  {r.discountGivenInPeriod
                    ? `-${money(r.discountGivenInPeriod)}`
                    : "—"}
                </td>
                <td>{money(r.revenueFromRedeemersInPeriod)}</td>
                <td>{r.newSubsAllTime}</td>
                <td>
                  {r.stillActiveAllTime}
                  {stillActivePct(r) && (
                    <small className="table-card__subtitle">
                      {" "}
                      {stillActivePct(r)}
                    </small>
                  )}
                </td>
              </tr>
            ))}
            {rows.length > 0 && (
              <tr className="growth-table__total">
                <td>
                  {t("growth.coupons.codesTotal", { count: rows.length })}
                </td>
                <td />
                <td />
                <td>{summary.redemptionsInPeriod}</td>
                <td>-{money(summary.discountGivenInPeriod)}</td>
                <td>{money(summary.revenueFromRedeemersInPeriod)}</td>
                <td>{summary.newSubsAllTime}</td>
                <td>
                  {summary.stillActiveAllTime}
                  {summary.newSubsAllTime > 0 && (
                    <small className="table-card__subtitle">
                      {" "}
                      {Math.round(
                        (summary.stillActiveAllTime / summary.newSubsAllTime) *
                          100
                      )}
                      %
                    </small>
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <p className="growth-card__footnote">
          {t("growth.coupons.footnote", { context })}
        </p>
      </section>
    </GrowthSection>
  );
};

export default GrowthCoupons;
